import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function ClaimCredits() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const { setUser } = useAuth(); // ✅ Moved to top level
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('loading');
  const [sessionData, setSessionData] = useState(null);

  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('ourpickup_session', sessionId);
      checkSession();
    } else {
      setStatus('no_session');
    }
  }, [sessionId]);

  const checkSession = async () => {
    try {
      const response = await fetch(`/api/session/status?sessionId=${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        setSessionData(data);
        setStatus('ready');
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Error checking session:', error);
      setStatus('error');
    }
  };

  const handleClaim = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/session/magic-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Magic login response:', data);
        
        localStorage.setItem('token', data.token);
        
        if (data.user) {
          setUser(data.user);
        }
        
        toast.success('✅ Credits claimed! Welcome to Our Pickup.');
        navigate('/buyer/dashboard');
      } else {
        const error = await response.json();
        console.error('Magic login error:', error);
        toast.error(error.error || 'Failed to claim credits');
      }
    } catch (error) {
      console.error('Error claiming credits:', error);
      toast.error('Failed to claim credits. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking your credits...</p>
        </div>
      </div>
    );
  }

  if (status === 'no_session') {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">🔗 No Session Found</h2>
        <p className="text-gray-600">This link doesn't have a valid session. Please check the link or try again.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
        >
          Go Home →
        </button>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="max-w-md mx-auto p-6 text-center">
        <h2 className="text-2xl font-bold mb-4">❌ Error</h2>
        <p className="text-gray-600">There was an error checking your session. Please try again.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700"
        >
          Go Home →
        </button>
      </div>
    );
  }

  const credits = sessionData?.pendingCredits || [];
  const totalCredits = credits.reduce((sum, c) => sum + c.amount, 0);

  return (
    <div className="max-w-md mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="text-6xl mb-4">📧</div>
        <h2 className="text-2xl font-bold mb-2">Your Credits Are Waiting!</h2>
        <p className="text-gray-600 mb-6">
          {totalCredits > 0 
            ? `You have ${totalCredits} credits and progress saved. Click below to claim them.`
            : 'You have credits and progress saved. Click below to claim them.'
          }
        </p>

        {totalCredits > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-lg font-medium text-green-700">💰 {totalCredits} Credits</p>
            <p className="text-sm text-green-600">
              {credits.length} pending credit{credits.length > 1 ? 's' : ''}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleClaim}
            disabled={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loading ? 'Claiming...' : '🔓 Claim My Credits'}
          </button>

          <p className="text-xs text-gray-400">
            No password needed. We'll log you in instantly.
          </p>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            Email: <span className="font-medium">{sessionData?.email || 'Not provided'}</span>
          </p>
        </div>
      </div>
    </div>
  );
}
